import {
  Alert,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  Menu,
  Portal,
  Table,
  Text,
} from '@chakra-ui/react'
import { FileAudio, Info, Pencil, Trash2 } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'

import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  ListToolbar,
  LoadingState,
  SortableColumnHeader,
} from '@mangovoice/mango-ui'
import type { TranslateFn } from '@mangovoice/mango-ui'

import type { SoundClip, SoundClipApi } from '../api/soundClipApi.types'
import { useGetSoundClips } from '../queries/soundClipQueries'
import { useDeleteSoundClip } from '../queries/soundClipQueries'
import { type SoundClipTranslateFn, text } from '../types/i18n.types'
import { useDebouncedCallback } from '../utils/useDebouncedCallback'

const DEFAULT_PAGE_SIZE = 25
const DEFAULT_ORDERING = 'name'

const formatFeatureCode = (code: number): string => {
  if (code === 0) return 'N/A'
  return `*${code}`
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return '—'
  }
}

const ThreeDotsIcon: React.FC = () => (
  <Icon boxSize={4}>
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  </Icon>
)

const ClipAudioPlayer: React.FC<{
  clip: SoundClip
  t?: TranslateFn | SoundClipTranslateFn
}> = ({ clip, t }) => {
  const [failed, setFailed] = useState(false)
  // Prefer the presigned file_url; the raw location 403s once the bucket
  // stops allowing anonymous reads.
  const src = clip.file_url ?? clip.location
  if (!src) return null

  if (failed) {
    return (
      <Text fontSize="sm" color="fg.error">
        {text(
          t,
          'soundClips.list.audioLoadError',
          "Audio couldn't be loaded — may need to be re-recorded.",
        )}
      </Text>
    )
  }

  return (
    <audio
      controls
      preload="none"
      onClick={(e) => e.stopPropagation()}
      onError={() => setFailed(true)}
      style={{
        height: 32,
        minWidth: 200,
        maxWidth: 300,
        borderRadius: 999,
      }}
    >
      {/* No type hint: uploads may be WAV or MP3 — let the browser sniff so a
          type mismatch doesn't trip the "couldn't load" fallback. */}
      <source src={src} onError={() => setFailed(true)} />
    </audio>
  )
}

const PaginationToolbar: React.FC<{
  pageSize: number | 'All'
  onPageSizeChange: (size: number | 'All') => void
  currentPage: number
  totalCount: number
  onPageChange: (page: number) => void
  isLoading: boolean
  mb?: number
}> = ({ mb, ...props }) => (
  <Box mb={mb}>
    <ListToolbar
      {...props}
      pageSizeOptions={[25, 50, 100]}
      showToolbar={props.totalCount > 0}
      size="sm"
    />
  </Box>
)

export interface SoundClipListPageProps {
  api: SoundClipApi
  pbxId: number
  onRowClick?: (clip: SoundClip) => void
  onCreateClick?: () => void
  t?: TranslateFn | SoundClipTranslateFn
}

export const SoundClipListPage: React.FC<SoundClipListPageProps> = ({
  api,
  pbxId,
  onRowClick,
  onCreateClick,
  t,
}) => {
  const [pageSize, setPageSize] = useState<number | 'All'>(DEFAULT_PAGE_SIZE)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [ordering, setOrdering] = useState(DEFAULT_ORDERING)
  const [deleteTarget, setDeleteTarget] = useState<SoundClip | null>(null)

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearch(value)
    setCurrentPage(1)
  }, 250)

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value)
      debouncedSetSearch(e.target.value)
    },
    [debouncedSetSearch],
  )

  const handleOrderingChange = useCallback((value: string) => {
    setOrdering(value)
    setCurrentPage(1)
  }, [])

  const handlePageSizeChange = useCallback((size: number | 'All') => {
    setPageSize(size)
    setCurrentPage(1)
  }, [])

  const limit = pageSize === 'All' ? 10000 : pageSize
  const offset = (currentPage - 1) * limit

  const queryParams = useMemo(
    () => ({
      pbxId,
      limit,
      offset,
      search: debouncedSearch || undefined,
      ordering,
    }),
    [pbxId, limit, offset, debouncedSearch, ordering],
  )

  const { data, isLoading, error } = useGetSoundClips(api, queryParams)
  const deleteMutation = useDeleteSoundClip(api, pbxId)

  const soundClips = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  const handleDelete = useCallback(
    async (clip: SoundClip) => {
      try {
        await deleteMutation.mutateAsync(clip.id)
      } catch {
        // error handled by mutation
      }
      setDeleteTarget(null)
    },
    [deleteMutation],
  )

  const renderContent = () => {
    if (isLoading && soundClips.length === 0) {
      return (
        <LoadingState
          layout="alert"
          title={text(t, 'soundClips.list.loading', 'Loading sound clips...')}
        />
      )
    }

    if (error) {
      return (
        <ErrorState
          layout="alert"
          title={text(t, 'soundClips.list.error', 'Error')}
          description={error.message}
        />
      )
    }

    if (soundClips.length === 0 && debouncedSearch) {
      return (
        <EmptyState
          layout="alert"
          title={text(t, 'soundClips.list.noResults', 'No results')}
          description={text(
            t,
            'soundClips.list.noResultsDescription',
            'No sound clips matching your search.',
          )}
        />
      )
    }

    if (soundClips.length === 0) {
      return (
        <EmptyState
          layout="alert"
          title={text(t, 'soundClips.list.empty', 'No sound clips found')}
          description={text(
            t,
            'soundClips.list.emptyDescription',
            'There are currently no sound clips configured for this PBX.',
          )}
        />
      )
    }

    return (
      <>
        <PaginationToolbar
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          currentPage={currentPage}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          isLoading={isLoading}
          mb={4}
        />
        <Table.Root size="sm" variant="outline">
          <Table.Header>
            <Table.Row borderBottom="1px solid" borderColor="border.default">
              <SortableColumnHeader
                label={text(t, 'soundClips.list.columnName', 'Name')}
                field="name"
                ordering={ordering}
                onOrderingChange={handleOrderingChange}
              />
              <SortableColumnHeader
                label={text(
                  t,
                  'soundClips.list.columnFeatureCode',
                  'Feature Code',
                )}
                field="feature_code"
                ordering={ordering}
                onOrderingChange={handleOrderingChange}
              />
              <Table.ColumnHeader px={4} py={3}>
                {text(t, 'soundClips.list.columnPreview', 'Preview')}
              </Table.ColumnHeader>
              <SortableColumnHeader
                label={text(t, 'soundClips.list.columnUpdated', 'Updated')}
                field="updated_at"
                ordering={ordering}
                onOrderingChange={handleOrderingChange}
              />
              <Table.ColumnHeader px={4} py={3} w="60px" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {soundClips.map((clip) => (
              <Table.Row
                key={clip.id}
                cursor={onRowClick ? 'pointer' : 'default'}
                onClick={() => onRowClick?.(clip)}
                borderBottom="1px solid"
                borderColor="border.default"
                _last={{ borderBottom: 'none' }}
                _hover={{ bg: 'bg.subtle' }}
                transition="background 0.15s"
              >
                <Table.Cell px={4} py={3} fontWeight="medium">
                  {clip.name}
                </Table.Cell>
                <Table.Cell px={4} py={3}>
                  <Badge
                    colorPalette={clip.feature_code ? 'info' : 'neutral'}
                    variant="subtle"
                  >
                    {formatFeatureCode(clip.feature_code)}
                  </Badge>
                </Table.Cell>
                <Table.Cell px={4} py={3}>
                  <ClipAudioPlayer clip={clip} t={t} />
                </Table.Cell>
                <Table.Cell px={4} py={3} color="fg.muted">
                  {formatDate(clip.updated_at)}
                </Table.Cell>
                <Table.Cell px={4} py={3} onClick={(e) => e.stopPropagation()}>
                  <Menu.Root>
                    <Menu.Trigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        borderRadius="lg"
                        px={2}
                      >
                        <ThreeDotsIcon />
                      </Button>
                    </Menu.Trigger>
                    <Portal>
                      <Menu.Positioner>
                        <Menu.Content>
                          <Menu.Item
                            value="edit"
                            onClick={() => onRowClick?.(clip)}
                            px={4}
                            py={2}
                          >
                            <Icon asChild mr={1}>
                              <Pencil size={16} />
                            </Icon>
                            {text(t, 'soundClips.list.edit', 'Edit')}
                          </Menu.Item>
                          <Menu.Item
                            value="delete"
                            onClick={() => setDeleteTarget(clip)}
                            color="fg.error"
                            px={4}
                            py={2}
                          >
                            <Icon asChild mr={1}>
                              <Trash2 size={16} />
                            </Icon>
                            {text(t, 'soundClips.list.delete', 'Delete')}
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Portal>
                  </Menu.Root>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>

        <PaginationToolbar
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          currentPage={currentPage}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          isLoading={isLoading}
        />
      </>
    )
  }

  return (
    <Box flex="1" w="full" p={6} overflow="auto">
      <HStack justify="space-between" mb={4}>
        <HStack gap={3}>
          <Icon as={FileAudio} boxSize={6} />
          <Heading size="lg">
            {text(t, 'soundClips.list.title', 'Sound Clips')}
          </Heading>
          {totalCount > 0 && (
            <Badge colorPalette="neutral" variant="subtle">
              {totalCount}
            </Badge>
          )}
        </HStack>
        <HStack gap={3} flex="1" justify="flex-end" minW="0">
          <Input
            placeholder={text(
              t,
              'soundClips.list.searchPlaceholder',
              'Search sound clips...',
            )}
            value={searchTerm}
            onChange={handleSearchChange}
            size="sm"
            borderRadius="md"
            maxW="xs"
          />
          {onCreateClick && (
            <Button
              size="sm"
              colorPalette="brand"
              bg="brand.solid"
              color="brand.contrast"
              borderRadius="lg"
              _hover={{ bg: 'brand.emphasized' }}
              _active={{ bg: 'brand.emphasized' }}
              onClick={onCreateClick}
            >
              {text(t, 'soundClips.list.createButton', '+ New Sound Clip')}
            </Button>
          )}
        </HStack>
      </HStack>

      <Alert.Root status="info" mb={4} size="sm">
        <Alert.Indicator>
          <Icon as={Info} />
        </Alert.Indicator>
        <Alert.Description>
          {text(
            t,
            'soundClips.list.recordingInfo',
            'Dial *99 from your phone to record a new sound clip. Dial *100–*109 to re-record an existing clip.',
          )}
        </Alert.Description>
      </Alert.Root>

      {renderContent()}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={text(
          t,
          'soundClips.list.deleteConfirmTitle',
          'Delete Sound Clip',
        )}
        description={text(
          t,
          'soundClips.list.deleteConfirmMessage',
          `Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`,
        ).replace('{{name}}', deleteTarget?.name ?? '')}
        confirmLabel={text(t, 'soundClips.list.deleteConfirmButton', 'Delete')}
        cancelLabel="Cancel"
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </Box>
  )
}
